import { Inject, Injectable, Optional } from '@angular/core';
import { FhirApiService } from '../api/fhir-api.sevice';
import { ConsentService } from './consent.service';
import { UserService } from '../user.service';
import { combineLatest, Observable, of } from 'rxjs';
import { map, mergeMap, take, tap } from 'rxjs/operators';
import { QuestionnaireEntryAdapter } from '../../data/questionnaires/questionnaire-entry.adapter';
import { QuestionnairesService } from '../../data/questionnaires/questionnaires.service';
import { QuestionnaireResponseService } from '../../data/questionnaires/questionnaire-repsonse/questionnaire-respone.service';
import { QuestionnaireResponse } from 'fhir/r4';
import { ReducerManager, Store } from '@ngrx/store';
import { QuestionnaireEntry } from '@core/data/questionnaires/questionnaire-entry.model';

interface QuestionnaireConsentEntity {
  resourceType: string;
  id: string;
}

interface ResourceWithProviderId {
  providerId: string;
  resources: QuestionnaireConsentEntity[];
}

@Injectable({ providedIn: 'root' })
export class ConsentQuestionnairesService extends QuestionnairesService {
  private consentContextLabel = 'readQuestionnaire';
  private providerId: string;
  private consentEntityIds$: Observable<string[]>;

  public dataWithConsent$: Observable<QuestionnaireEntry[]>;
  public dataWithoutConsent$: Observable<QuestionnaireEntry[]>;

  public selectedDataWithConsent$: Observable<QuestionnaireEntry[]>;
  public selectedDataWithoutConsent$: Observable<QuestionnaireEntry[]>;

  constructor(
    @Inject('dataname') @Optional() dataName: string,
    protected adapter: QuestionnaireEntryAdapter,
    protected fhirApi: FhirApiService,
    protected userService: UserService,
    protected consentService: ConsentService,
    protected fhirApiService: FhirApiService,
    protected questionnaireResponseService: QuestionnaireResponseService,
    protected store: Store,
    protected manager: ReducerManager,
  ) {
    super(
      dataName || 'consent-questionnaires',
      adapter,
      fhirApi,
      userService,
      store,
      manager,
    );
  }

  public init(providerId: string) {
    this.providerId = providerId;
    this.consentEntityIds$ = this.consentService.getConsentEntityIds$(
      this.providerId,
      this.consentContextLabel,
    );
    this.fetchDataByPatientId(this.consentService.patientId);
    this.bindObservableData();
  }

  private bindObservableData(): void {
    this.dataWithConsent$ = combineLatest([
      this.consentEntityIds$,
      this.data$,
    ]).pipe(
      map(([consentEntityIds, data]) => {
        return data.filter((entity) => consentEntityIds.includes(entity.id));
      }),
    );

    this.dataWithoutConsent$ = combineLatest([
      this.consentEntityIds$,
      this.data$,
    ]).pipe(
      map(([consentEntityIds, data]) =>
        this.getCompletedEntriesWithoutConsent(data, consentEntityIds),
      ),
    );

    this.selectedDataWithConsent$ = combineLatest([
      this.consentEntityIds$,
      this.selectedData$,
    ]).pipe(
      map(([consentEntityIds, data]) => {
        return data.filter((entity) => consentEntityIds.includes(entity.id));
      }),
    );

    this.selectedDataWithoutConsent$ = combineLatest([
      this.consentEntityIds$,
      this.selectedData$,
    ]).pipe(
      map(([consentEntityIds, data]) =>
        this.getCompletedEntriesWithoutConsent(data, consentEntityIds),
      ),
    );
  }

  private getCompletedEntriesWithoutConsent(
    entries: QuestionnaireEntry[],
    consentEntityIds: string[],
  ) {
    return entries
      .filter((entity) => !consentEntityIds.includes(entity.id))
      .filter((entity) => entity.status === 'completed');
  }

  private getQuestionnaireResponse$(serviceRequestId) {
    return this.questionnaireResponseService
      .searchItems$({ 'based-on': `ServiceRequest/${serviceRequestId}` })
      .pipe(
        map((result: QuestionnaireResponse[]) => {
          return {
            id: result[0].id,
            resourceType: result[0].resourceType,
          };
        }),
      );
  }

  private getQuestionnaireConsentEntities(questionnaires) {
    return combineLatest(
      questionnaires.map(
        (entry: QuestionnaireEntry): Observable<ResourceWithProviderId> => {
          return this.getQuestionnaireResponse$(entry.id).pipe(
            map((response) => {
              return {
                providerId: entry.practitionerId || this.providerId,
                resources: [
                  { resourceType: 'ServiceRequest', id: entry.id },
                  { resourceType: 'QuestionnaireResponse', id: response.id },
                ],
              };
            }),
          );
        },
      ),
    );
  }

  public addConsent(questionnaires): void {
    if (!this.providerId) return;
    this.addConsent$(questionnaires).pipe(take(1)).subscribe();
  }

  public addConsent$(questionnaires): Observable<any> {
    return this.dataWithConsent$.pipe(
      mergeMap((data: QuestionnaireEntry[]) =>
        this.getQuestionnaireConsentEntities(data.concat(questionnaires)),
      ),
      take(1),
      tap((items: any) => {
        const entities = items
          .filter(({ providerId }) => providerId === this.providerId)
          .map(({ resources }) => resources)
          .flat();

        const entityIds = this.getServiceRequestIdsOnly(entities);
        const validFrom = null;

        this.consentService.addConsent(
          this.providerId,
          entityIds,
          this.consentContextLabel,
          validFrom,
          entities,
        );
      }),
    );
  }

  public removeConsent(questionnaires: QuestionnaireEntry[]) {
    if (!this.providerId) return;

    this.dataWithConsent$
      .pipe(
        mergeMap((data: QuestionnaireEntry[]) => {
          const removed = data.filter((item) => questionnaires.includes(item));
          if (!removed?.length) return of([]);
          return this.getQuestionnaireConsentEntities(removed);
        }),
        take(1),
      )
      .subscribe((items: any) => {
        const entities =
          items
            .filter(({ providerId }) => providerId === this.providerId)
            .map(({ resources }) => resources)
            .flat() || [];

        const entityIds = entities.map(({ id }) => id);

        this.consentService.removeConsent(
          this.providerId,
          entityIds,
          this.consentContextLabel,
          entities,
        );
      });
  }

  private getServiceRequestIdsOnly(entities) {
    return entities
      .filter((entity) => entity.resourceType === 'ServiceRequest')
      .map(({ id }) => id);
  }
}
